import { useCallback } from 'react';

import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import IconButton from '@zenbusiness/zui/IconButton';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import PropTypes from 'prop-types';

import Steps from '#client/components/Steps';
import useToastContext from '#client/hooks/useToastContext';

import { DialogStyled, DialogTitleStyled, EmptyDivStyled } from './FormStepModal.styles';

const BackIcon = (
  <SharedAsset
    alt=""
    src="/icon/micro/caret-left.svg"
  />
);

export default function FormStepModal({
  children,
  currentStep,
  disableBack = false,
  disableCloseModal = false,
  handleClose,
  handleBack = () => {},
  isHideBackButton = false,
  stepNames,
  title,
  titleBackground,
  hideTitle = false
}) {
  const { clearAllToast } = useToastContext();

  const handleClearToastAndClose = useCallback(() => {
    if (!disableCloseModal) {
      handleClose();
    }
    clearAllToast();
  }, [clearAllToast, handleClose, disableCloseModal]);

  return (
    <DialogStyled
      disableRestoreFocus
      fullScreen
      onClose={handleClearToastAndClose}
      // when swipes should blur all active elements to hide the keyboard
      onTouchMoveCapture={() => document.activeElement.blur()}
      open
    >
      {!hideTitle && (
        <DialogTitleStyled
          component="div"
          titleBackground={titleBackground}
        >
          {!disableBack && !isHideBackButton ? (
            <IconButton
              aria-label="Go back one step in form modal"
              label="Go back one step in form modal"
              onClick={handleBack}
            >
              {BackIcon}
            </IconButton>
          ) : (
            <EmptyDivStyled />
          )}
          <Heading
            size="xl"
            weight="medium"
          >
            {title}
          </Heading>
          {disableCloseModal ? (
            <EmptyDivStyled />
          ) : (
            <IconButton
              iconSrc="/icon/micro/close-x.svg"
              label="Close form modal"
              onClick={handleClearToastAndClose}
              size="sm"
            />
          )}
        </DialogTitleStyled>
      )}
      <Box>
        {Array.isArray(stepNames) && stepNames.length > 0 && (
          <Steps
            currentStep={currentStep}
            stepNames={stepNames}
          />
        )}
      </Box>
      {children}
    </DialogStyled>
  );
}

FormStepModal.propTypes = {
  children: PropTypes.node.isRequired,
  currentStep: PropTypes.string,
  disableBack: PropTypes.bool,
  disableCloseModal: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  handleBack: PropTypes.func,
  isHideBackButton: PropTypes.bool,
  stepNames: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  title: PropTypes.string.isRequired,
  titleBackground: PropTypes.string,
  hideTitle: PropTypes.bool
};
